import React, { useState, useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Spinner, Center } from "@chakra-ui/react";
import { getSupabase, supabasePublic } from "./supabaseClient";
import { getBackendURL, basename } from "./env";
import {
  isAllowedRole,
  isInGroup,
  isPatientRole,
  canAccessPage,
  pageKeyForPath,
} from "../config/roles";

// Configuration: Set to true to disable protection (allow all routes), false to enable protection
const DISABLE_PROTECTED_ROUTE = true;

const ProtectedRoute = () => {
  console.log(
    "[ProtectedRoute] Component rendering, DISABLE_PROTECTED_ROUTE =",
    DISABLE_PROTECTED_ROUTE,
  );

  // Always call hooks unconditionally - Rules of Hooks require this
  const [loading, setLoading] = useState(true); // Track the loading state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const location = useLocation();

  // Always call useEffect unconditionally - Rules of Hooks require this
  useEffect(() => {
    console.log(
      "[ProtectedRoute] useEffect running, DISABLE_PROTECTED_ROUTE:",
      DISABLE_PROTECTED_ROUTE,
    );

    // ALWAYS-ON patient gate: an unapproved patient must never reach a protected
    // page, even while full route protection is disabled below. The only reliable
    // "approved" signal is patient_match_status === 'verified' (role isn't a
    // dependable proxy), so read it from public.users and redirect otherwise.
    const enforcePatientApproval = async () => {
      try {
        const { data: { session } } = await supabasePublic().auth.getSession();
        if (!session?.user?.id) return;
        const { data: pr } = await supabasePublic()
          .from("users")
          .select("role, patient_match_status")
          .eq("id", session.user.id)
          .single();
        if (isPatientRole(pr?.role) && pr?.patient_match_status !== "verified") {
          console.warn("[ProtectedRoute] Unapproved patient blocked → /auth/patient-verification");
          window.location.href =
            window.location.origin + (basename || "") + "/auth/patient-verification";
        }
      } catch (err) {
        console.warn("[ProtectedRoute] patient approval check failed:", err?.message);
      }
    };
    enforcePatientApproval();

    // Skip the remaining (full) protection logic if protection is disabled.
    if (DISABLE_PROTECTED_ROUTE) {
      console.log(
        "[ProtectedRoute] Protection disabled, setting loading false immediately",
      );
      setLoading(false);
      return;
    }

    const checkAuthStatus = async () => {
      console.log(
        "[ProtectedRoute] checkAuthStatus starting, calling getSupabase()...",
      );
      let supabaseClient;
      try {
        supabaseClient = await getSupabase();
        console.log(
          "[ProtectedRoute] getSupabase() returned",
          supabaseClient ? "client OK" : "null/undefined",
        );
      } catch (err) {
        console.error("[ProtectedRoute] getSupabase() threw error:", err);
        setLoading(false);
        return;
      }

      console.log(
        "[ProtectedRoute] Calling supabaseClient.auth.getSession()...",
      );
      let session, sessionError;
      try {
        const sessionPromise = supabaseClient.auth.getSession();
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(
            () => reject(new Error("getSession timed out after 5s")),
            5000,
          ),
        );
        const result = await Promise.race([sessionPromise, timeoutPromise]);
        session = result.data?.session;
        sessionError = result.error;
        console.log(
          "[ProtectedRoute] getSession() completed, session:",
          session ? "exists" : "none",
        );
      } catch (err) {
        console.error("[ProtectedRoute] getSession() failed:", err.message);
        sessionError = err;
      }

      // If no session from getSession(), try refreshing the session token
      // This handles cases where the session token has expired but Supabase client
      // still has a cached (stale) session.
      if (!session) {
        console.log(
          "[ProtectedRoute] No session from getSession(), attempting refreshSession()...",
        );
        const { data: refreshData, error: refreshError } =
          await supabaseClient.auth.refreshSession();
        if (refreshError) {
          console.warn(
            "[ProtectedRoute] Session refresh failed:",
            refreshError.message,
          );
        }
        if (refreshData?.session) {
          console.log("[ProtectedRoute] Session refreshed successfully!");
          session = refreshData.session;
          sessionError = null;
        }
      }

      if (!session || sessionError) {
        console.warn(
          "[ProtectedRoute] No valid session after refresh check - will redirect to 403",
        );
        setIsAuthenticated(false);
        setLoading(false);
        console.log("[ProtectedRoute] setLoading(false) called - no session");
        return;
      }

      console.log(
        "[ProtectedRoute] Session valid, checking user role in database...",
      );
      const { expires_at } = session;
      const currentTime = Math.floor(Date.now() / 1000);

      if (expires_at && expires_at > currentTime) {
        setIsAuthenticated(true);
        console.log(
          "[ProtectedRoute] Session not expired, querying users table for role...",
        );

        console.log("[ProtectedRoute] About to run users query...");
        // Role/name/schema only — selecting patient_* here would 42703 on tenant
        // schemas and force a public.users fallback that returns the wrong
        // (clinical) role for tenant-schema users.
        let { data, error } = await supabaseClient
          .from("users")
          .select("role, name, schema")
          .eq("id", session.user.id)
          .single();
        console.log("[ProtectedRoute] Users query returned!");

        console.log(
          "[ProtectedRoute] users query completed, error:",
          error ? error.message : "none",
          "data:",
          data ? "found" : "none",
        );

        // If the schema-specific query failed or returned nothing (e.g. STUDENT schema not yet
        // exposed in PostgREST cache — PGRST002), fall back to public.users which is always available.
        // Root cause: assign_user_schema RPC timed out, schema exists in DB but PostgREST
        // hasn't reloaded its schema cache yet, so queries to STUDENT/SOC_WORK/MISC.users fail.
        let usedPublicFallback = false;
        if (error || !data) {
          const errCode = error?.code || error?.hint || "";
          const isPGRST002 =
            errCode.includes("PGRST002") ||
            (error?.message || "").includes("PGRST002");
          console.warn(
            `⚠️ [ProtectedRoute] Schema-specific users query failed — falling back to public.users.\n` +
              `  Reason: ${error?.message || "no data returned"}\n` +
              `  Error code: ${errCode || "none"}\n` +
              `  Is PostgREST cache miss (PGRST002): ${isPGRST002}\n` +
              `  User ID: ${session.user.id}`,
            { error },
          );
          const fallback = await supabasePublic()
            .from("users")
            .select("role, name, schema")
            .eq("id", session.user.id)
            .single();
          if (fallback.error) {
            console.error(
              "❌ [ProtectedRoute] public.users fallback also failed:",
              fallback.error,
            );
          } else {
            data = fallback.data;
            error = null;
            usedPublicFallback = true;
            console.log(
              "✅ [ProtectedRoute] Got role from public.users fallback:",
              data?.role,
            );
          }
        }

        const normalizedRole = data?.role?.includes(",")
          ? data.role.split(",").pop().trim().toLowerCase()
          : String(data?.role || "").trim().toLowerCase();
        if (normalizedRole === "patient" && data?.role !== "patient") {
          window.location.href = window.location.origin + (basename || "") + "/auth/patient-verification";
          return;
        }

        if (error) {
          console.error("❌ [ProtectedRoute] Could not fetch user role — denying access:", error);
        } else if (!isAllowedRole(data?.role)) {
          console.warn(`🚫 [ProtectedRoute] → /403: role '${data?.role}' not in allowed roles.`);
          // Still trigger tenant-schema bootstrap if this is a pending tenant-schema user
          // (so when admin approves and the role changes from "user,X" to "X", the schema row exists)
          if (usedPublicFallback && data && isInGroup(data.role, "tenantSchema")) {
            const backendUrl = getBackendURL();
            const accountTypePending = data.role.includes(",")
              ? data.role.split(",")[1].trim()
              : data.role;
            const schemaCreationKey = `schema_created_${session.user.id}`;
            if (!sessionStorage.getItem(schemaCreationKey)) {
              console.log("🔄 [ProtectedRoute] Pending-approval tenant user — firing assign-user-schema in background");
              fetch(`${backendUrl}/api/assign-user-schema/`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ user_id: session.user.id, account_type: accountTypePending }),
              })
                .then((r) => r.ok ? (sessionStorage.setItem(schemaCreationKey, "true"), r) : r)
                .catch(() => {});
            }
          }
          setIsAuthorized(false);
        } else {
          // Role is allowed in the app. Now check page-level access.
          const pathForCheck =
            basename && location.pathname.startsWith(basename)
              ? location.pathname.slice(basename.length) || "/"
              : location.pathname;
          const pageKey = pageKeyForPath(pathForCheck);

          // Tenant-schema users: trigger background schema creation if needed
          // (preserves existing bootstrap behavior — this is NOT access logic)
          if (isInGroup(data.role, "tenantSchema")) {
            const accountType = data.role.includes(",")
              ? data.role.split(",")[1].trim()
              : data.role;
            const expectedSchema =
              accountType === "student" ? "STUDENT"
              : accountType === "socialworker" ? "SOC_WORK"
              : accountType === "medstudent" ? "MEDSTUD"
              : "MISC";
            const alreadyHasSchema = data.schema === expectedSchema;
            const backendUrl = getBackendURL();
            const schemaCreationKey = `schema_created_${session.user.id}`;
            const alreadyAttempted = sessionStorage.getItem(schemaCreationKey);
            if ((!alreadyHasSchema || usedPublicFallback) && !alreadyAttempted) {
              console.log("🚀 [ProtectedRoute] Calling backend to create schema...");
              fetch(`${backendUrl}/api/assign-user-schema/`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ user_id: session.user.id, account_type: accountType }),
              })
                .then((response) => {
                  if (response.ok) {
                    sessionStorage.setItem(schemaCreationKey, "true");
                    console.log("🎉 [ProtectedRoute] Schema assign OK");
                    return null;
                  }
                  return response;
                })
                .then(async (response) => {
                  if (response === null) return;
                  const contentType = response.headers.get("Content-Type") || "";
                  const isJson = contentType.includes("application/json");
                  try {
                    if (isJson) {
                      const result = await response.json();
                      console.error("❌ [ProtectedRoute] Backend returned error:", result);
                    } else {
                      console.error("❌ [ProtectedRoute] Non-JSON response from schema endpoint");
                    }
                  } catch (parseErr) {
                    console.warn("[ProtectedRoute] Could not parse response body:", parseErr);
                  }
                  sessionStorage.removeItem(schemaCreationKey);
                })
                .catch((err) => {
                  console.error("❌ [ProtectedRoute] Schema assign failed:", err);
                  sessionStorage.removeItem(schemaCreationKey);
                });
            }
          }

          if (pageKey == null) {
            setIsAuthorized(!isPatientRole(data.role));
          } else if (canAccessPage(data.role, pageKey)) {
            setIsAuthorized(true);
          } else {
            console.warn(
              `🚫 [ProtectedRoute] → /403: role '${data.role}' cannot access page '${pageKey}' (path: ${pathForCheck})`,
            );
            setIsAuthorized(false);
          }
        }

        // End of successful auth flow - set loading to false
        console.log(
          "[ProtectedRoute] Auth flow completed, setting loading false",
        );
        setLoading(false);
      } else {
        // Session exists but is expired - treat as not authenticated
        console.log(
          "[ProtectedRoute] Session expired, treating as not authenticated - will redirect to 403",
        );
        setIsAuthenticated(false);
        setLoading(false);
        console.log(
          "[ProtectedRoute] setLoading(false) called - session expired",
        );
      }
    };

    checkAuthStatus();
  }, [location.pathname]);

  // If protection is disabled, bypass all auth checks and allow access
  if (DISABLE_PROTECTED_ROUTE) {
    return <Outlet />;
  }

  if (loading) {
    return (
      <Center height="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (!isAuthenticated || !isAuthorized) {
    if (!isAuthenticated) {
      console.warn(
        "🚫 [ProtectedRoute] → /403 reason: no valid session found (not authenticated).",
      );
    } else {
      console.warn(
        "🚫 [ProtectedRoute] → /403 reason: isAuthorized=false. Check logs above for the specific reason.",
      );
    }
    return <Navigate to="/403" />;
  }

  return <Outlet />;
};

export default ProtectedRoute;
